import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { ACCENTURE_LOGO_SRC, injectTheme, T } from '../theme/theme';
import { useAuth } from '../hooks/useAuth';
import { auth_changePassword, auth_logout } from '../API';

const TOOL_META: Record<string, { label: string; badge: string }> = {
  BUILD_MDM_USING_DATA:          { label: 'Master Data Management', badge: 'MDM' },
  BUILD_DQ_USING_DATA:           { label: 'Data Quality',           badge: 'DQ'  },
  BUILD_DG_USING_DATA:           { label: 'Data Governance',        badge: 'GOV' },
  MIGRATION_INFA_LEGACY_TO_IDMC: { label: 'Migration → IDMC',       badge: 'MIG' },
  MIGRATION_INFA_LEGACY_TO_RELTIO: { label: 'Migration → Reltio',   badge: 'MIG' },
};

// ─── Change Password Modal ────────────────────────────────────────────────────
function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const [current,  setCurrent]  = useState('');
  const [pwd,      setPwd]      = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [error,    setError]    = useState('');
  const [msg,      setMsg]      = useState('');
  const [loading,  setLoading]  = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (pwd !== confirm) { setError('Passwords do not match'); return; }
    if (pwd.length < 8)  { setError('Minimum 8 characters');  return; }
    setLoading(true); setError('');
    try {
      await auth_changePassword(current, pwd, confirm);
      setMsg('Password changed successfully.');
      setTimeout(onClose, 1800);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="gw-cpwd-overlay" onClick={onClose}>
      <div className="gw-cpwd-modal" onClick={e => e.stopPropagation()}>
        <div className="gw-cpwd-hdr">
          <span className="gw-cpwd-title">Change password</span>
          <button className="gw-close-btn" onClick={onClose}>×</button>
        </div>
        <div className="gw-cpwd-body">
          {msg && (
            <div className="gw-admin-success">✓ {msg}</div>
          )}
          {error && (
            <div className="gw-admin-error">⚠ {error}</div>
          )}
          {!msg && (
            <form onSubmit={handleSubmit}>
              <div className="gw-form-group" style={{ marginBottom: 12 }}>
                <label className="gw-label">Current password</label>
                <input className="gw-input" type="password" value={current}
                  onChange={e => setCurrent(e.target.value)} required />
              </div>
              <div className="gw-form-group" style={{ marginBottom: 12 }}>
                <label className="gw-label">New password</label>
                <input className="gw-input" type="password" value={pwd}
                  minLength={8} onChange={e => setPwd(e.target.value)} required />
              </div>
              <div className="gw-form-group" style={{ marginBottom: 0 }}>
                <label className="gw-label">Confirm new password</label>
                <input className="gw-input" type="password" value={confirm}
                  minLength={8} onChange={e => setConfirm(e.target.value)} required />
              </div>
            </form>
          )}
        </div>
        {!msg && (
          <div className="gw-cpwd-foot">
            <button className="gw-btn gw-btn-ghost gw-btn-sm" onClick={onClose}>
              Cancel
            </button>
            <button className="gw-btn gw-btn-primary gw-btn-sm"
              onClick={handleSubmit as any} disabled={loading}>
              {loading ? 'Updating…' : 'Update password'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── User Menu ────────────────────────────────────────────────────────────────
function UserMenu({ onAdminClick }: { onAdminClick: () => void }) {
  const { user, setUser }         = useAuth();
  const [open, setOpen]           = useState(false);
  const [showCpwd, setShowCpwd]   = useState(false);
  const ref                       = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  async function handleLogout() {
    setOpen(false);
    await auth_logout();
    setUser(null);
  }

  if (!user) return null;

  const initials = user.full_name
    .split(' ')
    .map(w => w[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

  return (
    <>
      <div className="gw-user-menu" ref={ref}>
        <button
          className="gw-user-menu-trigger"
          onClick={() => setOpen(v => !v)}
        >
          <div className="gw-user-avatar">{initials}</div>
          <span style={{ maxWidth: 120, overflow: 'hidden',
                         textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user.full_name}
          </span>
          <span style={{ fontSize: 9, opacity: .6 }}>▾</span>
        </button>

        {open && (
          <div className="gw-user-dropdown">
            {/* User info */}
            <div className="gw-user-dropdown-info">
              <div className="gw-user-dropdown-name">{user.full_name}</div>
              <div className="gw-user-dropdown-email">{user.email}</div>
              <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
                {user.is_admin && (
                  <span className="gw-badge gw-badge-brand">Admin</span>
                )}
                <span className="gw-badge gw-badge-muted">
                  {user.last_login
                    ? `Last login: ${new Date(user.last_login).toLocaleDateString()}`
                    : 'First login'}
                </span>
              </div>
            </div>

            {/* Menu items */}
            <button
              className="gw-user-dropdown-item"
              onClick={() => { setShowCpwd(true); setOpen(false); }}
            >
              <span className="gw-user-dropdown-item-icon">🔑</span>
              Change password
            </button>

            {user.is_admin && (
              <button
                className="gw-user-dropdown-item"
                onClick={() => { onAdminClick(); setOpen(false); }}
              >
                <span className="gw-user-dropdown-item-icon">⚙️</span>
                User management
              </button>
            )}

            <div className="gw-user-dropdown-divider" />

            <button
              className="gw-user-dropdown-item danger"
              onClick={handleLogout}
            >
              <span className="gw-user-dropdown-item-icon">↩</span>
              Sign out
            </button>
          </div>
        )}
      </div>

      {showCpwd && (
        <ChangePasswordModal onClose={() => setShowCpwd(false)} />
      )}
    </>
  );
}

// ─── AppHeader ────────────────────────────────────────────────────────────────
interface AppHeaderProps {
  appName?:     string;
  tool?:        string;
  onHome?:      () => void;
  actions?:     ReactNode;
  center?:      ReactNode;
  onAdminOpen?: () => void;
}

const AppHeader: React.FC<AppHeaderProps> = ({
  appName,
  tool = '',
  onHome,
  actions,
  center,
  onAdminOpen,
}) => {
  useEffect(() => { injectTheme(); }, []);
  const meta = TOOL_META[tool];

  return (
    <header className="gw-app-header">
      {/* Brand */}
      <div className="gw-app-header-brand">
        <img src={ACCENTURE_LOGO_SRC} alt="Accenture" height={22} />
        <div className="gw-app-header-divider" />
        <div style={{ cursor: onHome ? 'pointer' : 'default' }} onClick={onHome}>
          <div className="gw-app-header-wordmark">Knowledge Suite</div>
          <div className="gw-app-header-sub">
            {meta ? meta.label : 'DMG Data Agents'}
          </div>
        </div>
        {appName && (
          <>
            <div className="gw-app-header-divider" />
            <span style={{ fontSize: 12, fontWeight: 600, color: T.textSecond,
                           fontFamily: "'Sora', system-ui, sans-serif",
                           maxWidth: 180, overflow: 'hidden',
                           textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={appName}>
              {appName}
            </span>
          </>
        )}
      </div>

      {/* Centre */}
      {center && (
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center',
                      paddingLeft: 16, paddingRight: 16 }}>
          {center}
        </div>
      )}

      {/* Right */}
      <div className="gw-app-header-right">
        {!meta && <span className="gw-app-header-pill">v2.0 Live</span>}
        {actions}
        <UserMenu onAdminClick={() => {
          if (onAdminOpen) {
            onAdminOpen();
          } else if ((window as any).__adriva_openAdmin) {
            (window as any).__adriva_openAdmin();
          }
        }} />
      </div>
    </header>
  );
};

export default AppHeader;